import React, { useEffect, useState } from 'react';
import { Button } from '@fiverr-private/button';
import { Center, Stack } from '@fiverr-private/layout_components';
import { ExternalLinkIcon } from '@fiverr-private/visuals';
import { Image } from '@fiverr-private/media';
import { ElementType } from '@fiverr-private/bianka_js';
import { getContext } from '@fiverr-private/fiverr_context';
import { useImpressionContainer, useImpressionItem } from '@fiverr-private/impressionable';
import { stepMetaMap, onImpression } from '@fiverr-private/store_builder_bi_events';
import { AppI18n } from '../../i18n';
import { useStepValidation } from '../../hooks/use-step-validation/useStepValidation';
import { verifySubscription } from '../../services/verifySubscription';
import { triggerStoreCreation } from '../../services/triggerStoreCreation';
import { useStoreData } from '../../hooks/useStoreData';
import { useWizardNavigation } from '../../hooks/use-wizard-navigation/useWizardNavigation';
import { replaceShopifyDomain } from '../../utils/replaceShopifyDomain';
import { reportClickEvent } from '../../utils/report-bi-event/reportClickEvent';
import { StepTitle } from '../../components/step-title/StepTitle';
import { UserNotAuthorizedError } from '../../errors/userNotAuthorized';
import { SubscribeModal } from './SubscribeModal';
import { IMAGE_URL, SHOPIFY_URL } from './Subscribe.constants';

export const Subscribe: React.FC = () => {
    const { pageCtxId, userId } = getContext();
    const { currentPath } = useWizardNavigation();
    const { registerStepValidation, unregisterStepValidation, registerValidationOnNext, unregisterValidationOnNext } =
        useStepValidation();
    const { storeData } = useStoreData();
    const [pageImpressionRef, pageImpressionId] = useImpressionContainer();

    const [hasClickedSubscribe, setHasClickedSubscribe] = useState(false);
    const [isSubscribeValid, setIsSubscribeValid] = useState(true);

    useImpressionItem({
        selfRef: pageImpressionRef,
        selfId: pageImpressionId,
        sendImpressionToBQ: false,
        enrichment: {
            eventType: stepMetaMap[currentPath].impressionName,
            componentName: stepMetaMap[currentPath].name,
        },
        intersectionThreshold: 0,
        onImpression,
    });

    useEffect(() => {
        const validationStatusFn = async () => {
            if (!storeData?.id) return 'invalid';

            try {
                const isValid = await verifySubscription(storeData.id);

                if (isValid) {
                    await triggerStoreCreation(storeData.id);
                }

                setIsSubscribeValid(isValid);
                return isValid ? 'valid' : 'invalid';
            } catch (error) {
                if (error instanceof UserNotAuthorizedError) {
                    return 'unauthenticated';
                }

                return 'invalid';
            }
        };

        registerValidationOnNext('subscribe', validationStatusFn);
        return () => {
            unregisterValidationOnNext('subscribe');
        };
    }, [registerValidationOnNext, unregisterValidationOnNext, storeData?.id]);

    useEffect(() => {
        const validationStatusFn = () => (hasClickedSubscribe ? 'valid' : 'invalid');

        registerStepValidation('subscribe', validationStatusFn);

        return () => {
            unregisterStepValidation('subscribe');
        };
    }, [hasClickedSubscribe, registerStepValidation, unregisterStepValidation]);

    const handleSubscribeClick = () => {
        setHasClickedSubscribe(true);
        reportClickEvent({
            biEventData: {
                userId,
                ctxId: pageCtxId,
            },
            eventType: 'clicked_on_subscribe_to_shopify',
            elementType: ElementType.LINK,
            currentPath,
        });

        if (!storeData?.storeUrl) return;
        const shopifyUrl = replaceShopifyDomain(SHOPIFY_URL, storeData.storeUrl);
        window.open(shopifyUrl, '_blank');
    };

    const handleModalClose = () => {
        setIsSubscribeValid(true);
    };

    const handleModalConfirm = () => {
        setIsSubscribeValid(true);
    };

    return (
        <Center>
            <Stack ref={pageImpressionRef} direction="column" gap="8" color="grey_1200">
                <StepTitle
                    title={<AppI18n k="steps.subscribe.title" />}
                    description={<AppI18n k="steps.subscribe.description" />}
                />
                <Image
                    src={IMAGE_URL}
                    width={{ default: 310, sm: 488, md: 514 }}
                    borderRadius="lg"
                    alt="Subscribe to Shopify"
                />
                <Button onClick={handleSubscribeClick} alignSelf="flexStart" variant="outline">
                    <AppI18n k="steps.subscribe.cta" />
                    <ExternalLinkIcon />
                </Button>
            </Stack>
            <SubscribeModal isOpen={!isSubscribeValid} onClose={handleModalClose} onConfirm={handleModalConfirm} />
        </Center>
    );
};

Subscribe.displayName = 'Subscribe';
